import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Divider, Alert, Snackbar, Tabs, Tab, TextField } from '@mui/material';
import { marked } from 'marked';
import MyNote from './MyNote';
import RichTextEditor from '../../../components/common/RichTextEditor';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../context/AuthContext';
import { withAuthRefresh } from '../../../utils/auth';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export default function ExplanationDialog({ open, onClose, question }) {
  const { user, userProfile, isEvaluator } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [isEditMode, setIsEditMode] = useState(false);
  
  // 문제 편집 상태
  const [editedQuestionText, setEditedQuestionText] = useState('');
  const [editedChoice1, setEditedChoice1] = useState('');
  const [editedChoice2, setEditedChoice2] = useState('');
  const [editedChoice3, setEditedChoice3] = useState('');
  const [editedChoice4, setEditedChoice4] = useState('');
  const [editedCorrectChoice, setEditedCorrectChoice] = useState(1);
  
  // 해설 편집 상태
  const [editedExplanation, setEditedExplanation] = useState('');
  const [editedExplanationEn, setEditedExplanationEn] = useState('');
  const [editedExplanationKo, setEditedExplanationKo] = useState('');
  
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    if (question && open) {
      // 문제 데이터 초기화
      setEditedQuestionText(question.question_text || '');
      setEditedChoice1(question.choice_1 || '');
      setEditedChoice2(question.choice_2 || '');
      setEditedChoice3(question.choice_3 || '');
      setEditedChoice4(question.choice_4 || '');
      setEditedCorrectChoice(question.correct_choice || 1);
      
      // 마크다운을 HTML로 변환하는 함수
      const convertMarkdownToHtml = (markdown) => {
        if (!markdown) return '';
        try {
          return marked(markdown, {
            breaks: true,
            gfm: true,
            tables: true,
            sanitize: false
          });
        } catch (error) {
          console.error('마크다운 변환 오류:', error);
          return markdown; // 오류 시 원본 반환
        }
      };
      
      // 해설 데이터 초기화 (마크다운을 HTML로 변환)
      setEditedExplanation(convertMarkdownToHtml(question.explanation || ''));
      setEditedExplanationEn(convertMarkdownToHtml(question.explanation_en || ''));
      setEditedExplanationKo(convertMarkdownToHtml(question.explanation_ko || ''));
      
      setIsEditMode(false);
      setTabValue(0);
    }
  }, [question, open]);



  if (!question) return null;

  const canEdit = isEvaluator || user?.id; // evaluator이거나 로그인한 사용자

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleEditToggle = () => {
    setIsEditMode(!isEditMode);
    if (!isEditMode) {
      // 편집 모드로 전환할 때 현재 값으로 초기화
      setEditedQuestionText(question.question_text || '');
      setEditedChoice1(question.choice_1 || '');
      setEditedChoice2(question.choice_2 || '');
      setEditedChoice3(question.choice_3 || '');
      setEditedChoice4(question.choice_4 || '');
      setEditedCorrectChoice(question.correct_choice || 1);
      
      // 마크다운을 HTML로 변환해서 에디터에 전달
      const convertMarkdownToHtml = (markdown) => {
        if (!markdown) return '';
        try {
          return marked(markdown, {
            breaks: true,
            gfm: true,
            tables: true,
            sanitize: false
          });
        } catch (error) {
          console.error('마크다운 변환 오류:', error);
          return markdown; // 오류 시 원본 반환
        }
      };
      
      setEditedExplanation(convertMarkdownToHtml(question.explanation || ''));
      setEditedExplanationEn(convertMarkdownToHtml(question.explanation_en || ''));
      setEditedExplanationKo(convertMarkdownToHtml(question.explanation_ko || ''));
    }
  };

  const handleSave = async () => {
    if (!canEdit) return;
    
    setIsSaving(true);
    setSaveError('');
    
    console.log('저장 시작...');
    
    try {
      await withAuthRefresh(async (user) => {
        console.log('사용자 인증 완료:', user.id);

        // HTML 내용 정리 (너무 긴 내용 방지)
        const cleanHtml = (html) => {
          if (!html) return '';
          // 기본 HTML 태그만 허용하고 나머지는 제거
          return html
            .replace(/<script[^>]*>.*?<\/script>/gi, '')
            .replace(/<style[^>]*>.*?<\/style>/gi, '')
            .replace(/javascript:/gi, '')
            .substring(0, 50000); // 50KB 제한
        };

        const updateData = {
          question_text: editedQuestionText?.substring(0, 10000) || '', // 10KB 제한
          choice_1: editedChoice1?.substring(0, 2000) || '',
          choice_2: editedChoice2?.substring(0, 2000) || '',
          choice_3: editedChoice3?.substring(0, 2000) || '',
          choice_4: editedChoice4?.substring(0, 2000) || '',
          correct_choice: parseInt(editedCorrectChoice) || 1,
          explanation: cleanHtml(editedExplanation),
          explanation_en: cleanHtml(editedExplanationEn),
          explanation_ko: cleanHtml(editedExplanationKo)
        };
        
        console.log('업데이트 데이터 준비 완료:', {
          question_id: question.id,
          explanation_length: updateData.explanation?.length || 0,
          explanation_en_length: updateData.explanation_en?.length || 0,
          explanation_ko_length: updateData.explanation_ko?.length || 0
        });
        
        // 권한 확인 (AuthContext의 userProfile 사용)  
        console.log('권한 확인 중...');
        
        if (!userProfile) {
          console.log('userProfile 없음, 데이터베이스에서 조회...');
          const { data: profileData, error: profileError } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', user.id)
            .single();
          
          console.log('프로필 조회 결과:', { profileData: !!profileData, profileError: !!profileError });
          
          if (profileError) {
            console.error('프로필 조회 오류:', profileError);
            throw new Error(`프로필 조회 실패: ${profileError.message}`);
          }
          
          const hasPermission = profileData && (
            profileData.role === 'admin' ||
            profileData.permissions?.can_evaluate_mee === true ||
            profileData.permissions?.can_evaluate_mpt === true
          );
          
          console.log('권한 확인 결과 (DB):', {
            role: profileData?.role,
            permissions: profileData?.permissions,
            hasPermission
          });
          
          if (!hasPermission) {
            throw new Error('문제 편집 권한이 없습니다. 관리자에게 문의하세요.');
          }
        } else {
          console.log('userProfile 사용:', userProfile);
          
          const hasPermission = userProfile && (
            userProfile.role === 'admin' ||
            userProfile.permissions?.can_evaluate_mee === true ||
            userProfile.permissions?.can_evaluate_mpt === true
          );
          
          console.log('권한 확인 결과 (userProfile):', {
            role: userProfile?.role,
            permissions: userProfile?.permissions,
            hasPermission
          });
          
          if (!hasPermission) {
            throw new Error('문제 편집 권한이 없습니다. 관리자에게 문의하세요.');
          }
        }

        // 데이터베이스 업데이트 실행
        console.log('데이터베이스 업데이트 시작...');
        console.log('업데이트할 문제 ID:', question.id);
        
        const { data, error } = await supabase
          .from('mbe_questions')
          .update(updateData)
          .eq('id', question.id)
          .select();
        
        console.log('업데이트 완료:', { 
          hasData: !!data, 
          hasError: !!error,
          dataLength: data?.length || 0
        });
        
        if (error) {
          console.error('업데이트 에러:', error);
          if (error.code === 'PGRST301' || error.message.includes('RLS')) {
            throw new Error('문제 편집 권한이 없습니다. RLS 정책을 확인하세요.');
          } else if (error.code === '42501') {
            throw new Error('데이터베이스 권한이 부족합니다. 관리자에게 문의하세요.');
          } else if (error.code === 'PGRST116') {
            throw new Error('해당 문제를 찾을 수 없습니다.');
          } else if (error.code === '23505') {
            throw new Error('중복된 데이터입니다.');
          } else if (error.code === '22001') {
            throw new Error('입력 데이터가 너무 깁니다. 내용을 줄여주세요.');
          } else {
            throw new Error(`저장 실패: ${error.message} (코드: ${error.code || 'UNKNOWN'})`);
          }
        }
        
        console.log('저장 성공!');
        
        // 성공 시 question 객체 업데이트
        Object.assign(question, updateData);
      });

      setIsEditMode(false);
      setSaveSuccess(true);
      
      // 3초 후 성공 메시지 자동 숨김
      setTimeout(() => setSaveSuccess(false), 3000);
      
    } catch (error) {
      console.error('문제 저장 오류:', error);
      
      if (error.message?.includes('인증이 필요합니다')) {
        setSaveError('로그인이 만료되었습니다. 페이지를 새로고침해주세요.');
      } else {
        setSaveError(error.message || '문제 저장 중 오류가 발생했습니다.');
      }
      
      // 10초 후 에러 메시지 자동 숨김
      setTimeout(() => setSaveError(''), 10000);
    } finally {
      console.log('저장 프로세스 완료');
      setIsSaving(false);
    }
  };

  // 강제 리셋 함수 (디버깅용)
  const handleForceReset = () => {
    console.log('강제 리셋 실행');
    setIsSaving(false);
    setSaveError('');
  };





  const handleCancel = () => {
    setEditedQuestionText(question.question_text || '');
    setEditedChoice1(question.choice_1 || '');
    setEditedChoice2(question.choice_2 || '');
    setEditedChoice3(question.choice_3 || '');
    setEditedChoice4(question.choice_4 || '');
    setEditedCorrectChoice(question.correct_choice || 1);
    
    // 마크다운을 HTML로 변환해서 에디터에 전달
    const convertMarkdownToHtml = (markdown) => {
      if (!markdown) return '';
      try {
        return marked(markdown, {
          breaks: true,
          gfm: true,
          tables: true,
          sanitize: false
        });
      } catch (error) {
        console.error('마크다운 변환 오류:', error);
        return markdown; // 오류 시 원본 반환
      }
    };
    
    setEditedExplanation(convertMarkdownToHtml(question.explanation || ''));
    setEditedExplanationEn(convertMarkdownToHtml(question.explanation_en || ''));
    setEditedExplanationKo(convertMarkdownToHtml(question.explanation_ko || ''));
    setIsEditMode(false);
  };

  const explanationParts = [
    { title: '원문 해설', content: question.explanation },
    { title: '영문 해설', content: question.explanation_en },
    { title: '한글 해설', content: question.explanation_ko },
  ]
  .filter(item => item.content)
  .map(item => `### ${item.title}\n\n${item.content}`)
  .join('\n\n<hr />\n\n');

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose} 
        fullWidth 
        maxWidth="lg"
        aria-labelledby="explanation-dialog-title"
      >
        <DialogTitle id="explanation-dialog-title">
          문제 상세 정보
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs value={tabValue} onChange={handleTabChange} aria-label="문제 편집 탭">
              <Tab label="문제 & 선택지" />
              <Tab label="해설" />
            </Tabs>
          </Box>

          <TabPanel value={tabValue} index={0}>
            {!isEditMode ? (
              <Box>
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ mb: 1, fontWeight: 'bold', color: 'primary.main' }}>
                    문제
                  </Box>
                  <Box sx={{ background: '#f9f9f9', p: 2, borderRadius: 1, whiteSpace: 'pre-line' }}>
                    {question.question_text}
                  </Box>
                </Box>
                
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ mb: 1, fontWeight: 'bold', color: 'primary.main' }}>
                    선택지
                  </Box>
                  <Box sx={{ background: '#f9f9f9', p: 2, borderRadius: 1 }}>
                    {['A', 'B', 'C', 'D'].map((label, index) => (
                      <Box 
                        key={label} 
                        sx={{ 
                          mb: 1, 
                          p: 1, 
                          borderRadius: 1,
                          backgroundColor: question.correct_choice === index + 1 ? '#e8f5e8' : 'transparent',
                          border: question.correct_choice === index + 1 ? '1px solid #4caf50' : 'none'
                        }}
                      >
                        <strong>{label}.</strong> {question[`choice_${index + 1}`]}
                        {question.correct_choice === index + 1 && (
                          <Box component="span" sx={{ ml: 1, color: 'success.main', fontWeight: 'bold' }}>
                            (정답)
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            ) : (
              <Box>
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ mb: 1, fontWeight: 'bold', color: 'primary.main' }}>
                    문제
                  </Box>
                  <TextField
                    fullWidth
                    multiline
                    rows={6}
                    value={editedQuestionText}
                    onChange={(e) => setEditedQuestionText(e.target.value)}
                    placeholder="문제를 입력하세요..."
                    variant="outlined"
                  />
                </Box>
                
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ mb: 1, fontWeight: 'bold', color: 'primary.main' }}>
                    선택지
                  </Box>
                  {['A', 'B', 'C', 'D'].map((label, index) => (
                    <Box key={label} sx={{ mb: 2 }}>
                      <TextField
                        fullWidth
                        label={`선택지 ${label}`}
                        value={index === 0 ? editedChoice1 : index === 1 ? editedChoice2 : index === 2 ? editedChoice3 : editedChoice4}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (index === 0) setEditedChoice1(value);
                          else if (index === 1) setEditedChoice2(value);
                          else if (index === 2) setEditedChoice3(value);
                          else setEditedChoice4(value);
                        }}
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: editedCorrectChoice === index + 1 ? '#e8f5e8' : 'transparent'
                          }
                        }}
                      />
                      <Button
                        size="small"
                        variant={editedCorrectChoice === index + 1 ? 'contained' : 'outlined'}
                        color="success"
                        onClick={() => setEditedCorrectChoice(index + 1)}
                        sx={{ mt: 1 }}
                      >
                        {editedCorrectChoice === index + 1 ? '정답' : '정답으로 설정'}
                      </Button>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            {!isEditMode ? (
              <Box>
                {[
                  { title: '원문 해설', content: question.explanation },
                  { title: '영문 해설', content: question.explanation_en },
                  { title: '한글 해설', content: question.explanation_ko },
                ].filter(item => item.content).map((item, index) => (
                  <Box key={index} sx={{ mb: 3 }}>
                    <Box sx={{ mb: 1, fontWeight: 'bold', color: 'primary.main', fontSize: '1.1em' }}>
                      {item.title}
                    </Box>
                    <Box sx={{ background: '#f9f9f9', p: 2, borderRadius: 1 }}>
                      <Box 
                        sx={{ 
                          '& img': {
                            maxWidth: '100%',
                            height: 'auto',
                            borderRadius: '4px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            margin: '8px 0',
                          },
                          '& a': {
                            color: '#1976d2',
                            textDecoration: 'underline',
                            '&:hover': {
                              color: '#1565c0',
                            },
                          },
                          '& h1, & h2, & h3': {
                            color: '#333',
                            marginTop: '16px',
                            marginBottom: '8px',
                          },
                          '& p': {
                            marginBottom: '8px',
                            lineHeight: 1.6,
                          },
                          '& ul, & ol': {
                            paddingLeft: '20px',
                            marginBottom: '8px',
                          },
                          '& blockquote': {
                            borderLeft: '4px solid #ddd',
                            paddingLeft: '16px',
                            margin: '16px 0',
                            color: '#666',
                            fontStyle: 'italic',
                          },
                          '& table': {
                            width: '100%',
                            borderCollapse: 'collapse',
                            margin: '16px 0',
                            border: '1px solid #ddd',
                          },
                          '& th, & td': {
                            border: '1px solid #ddd',
                            padding: '8px 12px',
                            textAlign: 'left',
                          },
                          '& th': {
                            backgroundColor: '#f5f5f5',
                            fontWeight: 'bold',
                          },
                          '& tbody tr:nth-of-type(even)': {
                            backgroundColor: '#f9f9f9',
                          },
                        }}
                        dangerouslySetInnerHTML={{ 
                          __html: (() => {
                            try {
                              let content = item.content || '해설이 제공되지 않았습니다.';
                              console.log('원본 내용:', content);
                              
                              // 마크다운을 HTML로 완전 변환 (테이블 포함)
                              const htmlContent = marked(content, {
                                breaks: true,
                                gfm: true,
                                tables: true,
                                sanitize: false
                              });
                              
                              console.log('변환된 내용:', htmlContent);
                              return htmlContent;
                            } catch (error) {
                              console.error('마크다운 변환 오류:', error);
                              // 오류 시 기존 방식으로 폴백
                              let content = item.content || '해설이 제공되지 않았습니다.';
                              
                              // HTML에서 마크다운 이미지 문법을 img 태그로 변환
                              content = content.replace(
                                /!\[([^\]]*)\]\(([^)]+)\)/g, 
                                '<img src="$2" alt="$1" style="max-width: 100%; height: auto; border-radius: 4px; box-shadow: 0 2px 8px rgba(0,0,0,0.1); margin: 8px 0;" />'
                              );
                              
                              // HTML에서 마크다운 링크 문법을 a 태그로 변환
                              content = content.replace(
                                /\[([^\]]+)\]\(([^)]+)\)/g,
                                '<a href="$2" style="color: #1976d2; text-decoration: underline;">$1</a>'
                              );
                              
                              return content;
                            }
                          })()
                        }}
                      />
                    </Box>
                  </Box>
                ))}
                {![question.explanation, question.explanation_en, question.explanation_ko].some(Boolean) && (
                  <Box sx={{ background: '#f9f9f9', p: 2, borderRadius: 1, textAlign: 'center', color: '#666' }}>
                    해설이 제공되지 않았습니다.
                  </Box>
                )}
              </Box>
            ) : (
              <Box sx={{ mb: 2 }}>
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ mb: 1, fontWeight: 'bold', color: 'primary.main' }}>
                    원문 해설
                  </Box>
                  <RichTextEditor
                    value={editedExplanation}
                    onChange={setEditedExplanation}
                    placeholder="원문 해설을 입력하세요..."
                    minHeight={150}
                  />
                </Box>
                
                <Divider sx={{ my: 2 }} />
                
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ mb: 1, fontWeight: 'bold', color: 'primary.main' }}>
                    영문 해설
                  </Box>
                  <RichTextEditor
                    value={editedExplanationEn}
                    onChange={setEditedExplanationEn}
                    placeholder="영문 해설을 입력하세요..."
                    minHeight={150}
                  />
                </Box>
                
                <Divider sx={{ my: 2 }} />
                
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ mb: 1, fontWeight: 'bold', color: 'primary.main' }}>
                    한글 해설
                  </Box>
                  <RichTextEditor
                    value={editedExplanationKo}
                    onChange={setEditedExplanationKo}
                    placeholder="한글 해설을 입력하세요..."
                    minHeight={150}
                  />
                </Box>
              </Box>
            )}
          </TabPanel>
          
          <Divider sx={{ my: 2 }} />
          <MyNote questionId={question.id} />
        </DialogContent>
        <DialogActions>
          {canEdit && (
            <>
              {!isEditMode ? (
                <Button onClick={handleEditToggle} color="primary" variant="outlined">
                  문제 편집
                </Button>
              ) : (
                <>
                  <Button onClick={handleCancel} color="secondary">
                    취소
                  </Button>
                  <Button 
                    onClick={handleSave} 
                    color="primary" 
                    variant="contained"
                    disabled={isSaving}
                    sx={{ 
                      minWidth: 120,
                      position: 'relative'
                    }}
                  >
                    {isSaving ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box 
                          sx={{ 
                            width: 16, 
                            height: 16, 
                            border: '2px solid #fff',
                            borderTop: '2px solid transparent',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite',
                            '@keyframes spin': {
                              '0%': { transform: 'rotate(0deg)' },
                              '100%': { transform: 'rotate(360deg)' }
                            }
                          }} 
                        />
                        저장 중...
                      </Box>
                    ) : '저장'}
                  </Button>
                  {isSaving && (
                    <Button 
                      onClick={handleForceReset} 
                      color="warning" 
                      variant="outlined"
                      size="small"
                      sx={{ ml: 1 }}
                    >
                      🔄 리셋
                    </Button>
                  )}
                </>
              )}
            </>
          )}
          <Button onClick={onClose} color="primary">
            닫기
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={saveSuccess}
        autoHideDuration={3000}
        onClose={() => setSaveSuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSaveSuccess(false)} severity="success" sx={{ width: '100%' }}>
          문제가 성공적으로 저장되었습니다!
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!saveError}
        autoHideDuration={5000}
        onClose={() => setSaveError('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSaveError('')} severity="error" sx={{ width: '100%' }}>
          {saveError}
        </Alert>
      </Snackbar>
    </>
  );
} 